import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker, ConfigProvider } from 'antd';
import koKR from 'antd/locale/ko_KR';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  getPromotions,
  getPromotionDetail,
  savePromotion,
  deletePromotion,
  getPromotionProducts,
  getManagers,
  getUserCompanyInfo,
  uploadAllSizeS3,
  getSupplierCities,
  getCommonImagesByCity,
  getProductGallery,
} from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';
import { mirrorPromotion, mirrorPromotionDelete } from '../services/promotionMirrorApi';

dayjs.locale('ko');

/* 기획전 관리
 * ---------------------------------------------------------------------------
 * 기획전 자체의 CRUD와 연동 상품 조회를 담당한다. 날짜(가격번호) 단위 매핑은
 * 가격/재고 화면에서 처리한다 — 기획전에 담긴 개별 날짜를 조회하는 SP가 없어
 * 여기서는 상품 단위까지만 보여주고, 해당 상품의 가격/재고로 이동시킨다.
 *
 * 소유 업체 필터
 * ---------------------------------------------------------------------------
 * RCT_S_기획전은 로그인 업체의 기획전에 본사(업체번호 1)의 공용 기획전을 섞어서
 * 내려준다(@공급업체번호 = 1 이면 전 업체 기획전). 그런데 SELECT 목록에
 * 공급업체번호가 없고 `담당업체 = 업체명`만 있어서, 소유 판별은 업체명 대조로만
 * 가능하다. 이름은 내 업체번호로 조회한 CW_S_유저정보에서 가져오므로 토큰에서
 * 파생된 값이고, 남는 위험은 서로 다른 업체가 완전히 동일한 업체명을 쓰는 경우뿐이다.
 * 업체명을 확정하지 못하면 목록을 아예 표시하지 않는다(fail-closed).
 *
 * 편집/삭제 권한은 UI와 무관하게 SP가 강제한다 —
 * RCT_T_기획전 / RCT_T_기획전삭제가 등록 공급업체번호를 대조해 RAISERROR로 거부한다.
 *
 * 기획전 이미지(기획전URL)
 * ---------------------------------------------------------------------------
 * 대리점 포털 카드 배경으로 쓰인다. TourDetailPageSettings 이미지 추가와 같이
 * PC/공용/상품갤러리에서 고르면 URL이 자동 삽입되고, UI에는 썸네일만 보여 준다.
 * PC 업로드 FilePath 는 고정: 1/Product/111366/Editor/
 */

const PROMO_IMAGE_FILE_PATH = '1/Product/111366/Editor/';
const PROMO_GALLERY_PRODUCT_NO = 111366;

const imageSrcOf = (img) => String(img?.이미지경로2 || img?.이미지경로 || '').trim();

const YMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const shiftYears = (years) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return YMD(d);
};

/** 연-월-일 표시 + 캘린더 피커 (브라우저 locale 무관) */
const YmdDatePicker = ({ value, onChange, className = '', disabled = false, allowClear = false }) => (
  <DatePicker
    className={className || 'w-full'}
    format="YYYY-MM-DD"
    value={value && dayjs(value, 'YYYY-MM-DD', true).isValid() ? dayjs(value, 'YYYY-MM-DD') : null}
    onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : '')}
    disabled={disabled}
    allowClear={allowClear}
    inputReadOnly
  />
);

// 기획전_수수료규정의 요율은 비율(0.1 = 10%)로 저장된다. SP가 저장 시
// `CASE WHEN 수수료 < 100 THEN 수수료/100 ELSE 수수료 END` 를 적용하므로
// 100 이상은 나누기가 걸리지 않는다. 그래서 입력을 0~99.99로 제한한다.
const MAX_RATE = 99.99;

const clampRate = (value) => {
  const s = String(value ?? '').replace(/[^0-9.]/g, '');
  if (s === '') return '';
  const parts = s.split('.');
  const normalized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : s;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return '';
  if (n > MAX_RATE) return String(MAX_RATE);
  return normalized;
};

const PromotionManagement = () => {
  const navigate = useNavigate();
  const { userNo, companyNo } = getUserInfo() || {};

  const [startDate, setStartDate] = useState(shiftYears(-1));
  const [endDate, setEndDate] = useState(shiftYears(1));
  const [includeClosed, setIncludeClosed] = useState(true);
  const [keyword, setKeyword] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [managers, setManagers] = useState([]);

  // undefined = 확인 중, null = 확인 실패, string = 확정된 내 업체명
  const [myCompanyName, setMyCompanyName] = useState(undefined);

  // 편집 모달
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  // 기획전 이미지 선택 (TourDetailPageSettings 이미지 추가와 동일 흐름)
  const [imageMenuOpen, setImageMenuOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const imageMenuRef = useRef(null);
  const imageFileInputRef = useRef(null);

  const [commonOpen, setCommonOpen] = useState(false);
  const [commonLoading, setCommonLoading] = useState(false);
  const [commonImgs, setCommonImgs] = useState([]);
  const [commonCities, setCommonCities] = useState([]);
  const [commonCityNo, setCommonCityNo] = useState('');
  const [commonCityName, setCommonCityName] = useState('');
  const [commonCityQuery, setCommonCityQuery] = useState('');
  const [commonCityOpen, setCommonCityOpen] = useState(false);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryImgs, setGalleryImgs] = useState([]);

  // 연동 상품 모달
  const [productsOpen, setProductsOpen] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsTarget, setProductsTarget] = useState(null);
  const [products, setProducts] = useState([]);

  const setPromoImageUrl = useCallback((url) => {
    const next = String(url || '').trim();
    setForm((prev) => (prev ? { ...prev, 기획전URL: next } : prev));
    setImageMenuOpen(false);
    setCommonOpen(false);
    setGalleryOpen(false);
  }, []);

  const handlePromoUploadFromPc = useCallback(() => {
    setImageMenuOpen(false);
    imageFileInputRef.current?.click();
  }, []);

  const onPromoImageFileChange = useCallback(async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    try {
      setImageUploading(true);
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result || '';
          const base64 = String(result).includes(',') ? String(result).split(',')[1] : String(result);
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const uploadRes = await uploadAllSizeS3({
        fileBase64,
        fileName: file.name,
        filePath: PROMO_IMAGE_FILE_PATH,
      });
      const imagePath = uploadRes?.url || `https://kr.object.iwinv.kr/${PROMO_IMAGE_FILE_PATH}${file.name}`;
      setPromoImageUrl(imagePath);
    } catch (err) {
      console.error(err);
      window.alert('이미지 업로드에 실패했습니다.');
    } finally {
      setImageUploading(false);
    }
  }, [setPromoImageUrl]);

  const openPromoCommonModal = useCallback(async () => {
    setImageMenuOpen(false);
    setCommonOpen(true);
    setCommonLoading(true);
    setCommonCityOpen(false);
    setCommonCityQuery('');
    try {
      const { companyNo: cNo } = getUserInfo() || {};
      const cityList = cNo ? await getSupplierCities({ companyNo: cNo }).catch(() => []) : [];
      const cities = Array.isArray(cityList) ? cityList : [];
      setCommonCities(cities);
      const first = cities[0];
      const cityNo = first ? String(first.코드 || '') : '';
      const cityName = first ? String(first.코드명 || '') : '';
      setCommonCityNo(cityNo);
      setCommonCityName(cityName);
      if (cityNo) {
        const imgs = await getCommonImagesByCity({ cityNo });
        setCommonImgs(Array.isArray(imgs) ? imgs : []);
      } else {
        setCommonImgs([]);
      }
    } catch (err) {
      console.error(err);
      window.alert('공용 이미지를 불러오지 못했습니다.');
      setCommonImgs([]);
    } finally {
      setCommonLoading(false);
    }
  }, []);

  const openPromoGalleryModal = useCallback(async () => {
    setImageMenuOpen(false);
    setGalleryOpen(true);
    setGalleryLoading(true);
    try {
      const { companyNo: cNo } = getUserInfo() || {};
      const imgs = await getProductGallery({
        companyNo: cNo,
        productNo: PROMO_GALLERY_PRODUCT_NO,
      }).catch(() => []);
      setGalleryImgs(Array.isArray(imgs) ? imgs : []);
    } catch (err) {
      console.error(err);
      window.alert('상품 갤러리 이미지를 불러오지 못했습니다.');
      setGalleryImgs([]);
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!imageMenuOpen) return undefined;
    const onDown = (e) => {
      if (imageMenuRef.current && !imageMenuRef.current.contains(e.target)) {
        setImageMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [imageMenuOpen]);

  const load = useCallback(async () => {
    // 업체명이 확정되기 전에는 조회하지 않는다. 먼저 그렸다가 필터링하면
    // 타 업체 기획전이 한 프레임 노출된다.
    if (myCompanyName === undefined) return;
    if (!myCompanyName) {
      setRows([]);
      setLoading(false);
      setError('로그인 업체 정보를 확인할 수 없어 기획전을 표시할 수 없습니다. 새로고침 후 다시 시도해 주세요.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const list = await getPromotions({ startDate, endDate, includeClosed });
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || '기획전 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, includeClosed, myCompanyName]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const list = await getManagers();
        setManagers(Array.isArray(list) ? list : []);
      } catch (_) {
        setManagers([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const info = await getUserCompanyInfo();
        const name = String(info?.업체명 ?? '').trim();
        setMyCompanyName(name || null);
      } catch (_) {
        setMyCompanyName(null);
      }
    })();
  }, []);

  // 내 업체가 등록한 기획전인지. 업체명을 확정하지 못하면 항상 false —
  // 소유를 증명할 수 없는 행은 표시도, 편집/삭제 UI도 내주지 않는다.
  const isMine = useCallback(
    (row) => !!myCompanyName && String(row?.담당업체 ?? '').trim() === myCompanyName,
    [myCompanyName],
  );

  const owned = useMemo(() => rows.filter(isMine), [rows, isMine]);

  const filtered = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    if (!text) return owned;
    return owned.filter((r) => (
      String(r?.기획전명 || '').toLowerCase().includes(text)
      || String(r?.담당자 || '').toLowerCase().includes(text)
    ));
  }, [owned, keyword]);

  const openForm = async (promotionNo) => {
    setFormOpen(true);
    setFormLoading(true);
    setForm(null);
    try {
      // 신규(0)일 때도 SP가 빈 껍데기와 상품종류 3행을 내려준다.
      // 상품종류 코드를 프론트에 하드코딩하지 않기 위해 항상 SP를 경유한다.
      const info = await getPromotionDetail({ promotionNo: promotionNo || 0 });
      if (!info) {
        setFormOpen(false);
        window.alert('기획전 정보를 불러오지 못했습니다.');
        return;
      }
      const isNew = !promotionNo;
      setForm({
        기획전번호: Number(info.기획전번호) || 0,
        기획전명: info.기획전명 || '',
        시작일: info.시작일 || '',
        종료일: info.종료일 || '',
        담당자번호: String(info.담당자번호 || (isNew ? (userNo ?? '') : '')),
        기획전URL: info.기획전주소 || '',
        마감상태: !!info.마감상태,
        기획전설명: info.기획전설명 || '',
        이용약관: info.이용약관 || '',
        수수료규정: (info.상품종류수수료규정 || []).map((x) => ({
          상품종류: x.상품종류 || '',
          상품종류코드: String(x.상품종류코드 || ''),
          적용여부: !!x.적용여부,
          대리점수수료: String(Number(x.대리점수수료) || 0),
          플랫폼수수료: String(Number(x.플랫폼수수료) || 0),
        })),
      });
    } catch (e) {
      setFormOpen(false);
      window.alert(e?.message || '기획전 정보를 불러오지 못했습니다.');
    } finally {
      setFormLoading(false);
    }
  };

  const setField = (key, value) => setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const setRule = (code, key, value) => setForm((prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      수수료규정: prev.수수료규정.map((r) => (r.상품종류코드 === code ? { ...r, [key]: value } : r)),
    };
  });

  const submit = async () => {
    if (!form) return;
    const name = form.기획전명.trim();
    if (!name) { window.alert('기획전명을 입력하세요.'); return; }
    if (!form.시작일 || !form.종료일) { window.alert('시작일과 종료일을 입력하세요.'); return; }
    if (form.시작일 > form.종료일) { window.alert('종료일이 시작일보다 앞설 수 없습니다.'); return; }
    if (!form.담당자번호) { window.alert('담당자를 선택하세요.'); return; }

    const overflow = form.수수료규정.find((r) => Number(r.대리점수수료) > MAX_RATE || Number(r.플랫폼수수료) > MAX_RATE);
    if (overflow) {
      window.alert(`수수료는 ${MAX_RATE}%까지 입력할 수 있습니다. (${overflow.상품종류})`);
      return;
    }

    try {
      setSaving(true);
      const saved = await savePromotion({
        promotionNo: form.기획전번호,
        managerNo: form.담당자번호,
        closed: form.마감상태,
        name,
        startDate: form.시작일,
        endDate: form.종료일,
        description: form.기획전설명,
        url: form.기획전URL,
        terms: form.이용약관,
        commissionRules: form.수수료규정.map((r) => ({
          적용여부: r.적용여부,
          상품종류코드: r.상품종류코드,
          대리점수수료: Number(r.대리점수수료) || 0,
          플랫폼수수료: Number(r.플랫폼수수료) || 0,
        })),
      });

      // 대리점 포털(jn_crs) 노출용 Supabase 미러. ERP 저장이 원본이고 여기는 복제본이라
      // 실패해도 저장을 되돌리지 않는다 — 다음 저장 때 다시 맞춰진다.
      // 신규 등록이면 SP가 돌려준 행에서 새 기획전번호를 얻는다.
      const savedNo = Number(saved?.기획전번호) || form.기획전번호;
      if (savedNo) {
        const mirrored = await mirrorPromotion({
          promotionNo: savedNo,
          companyNo,
          name,
          startDate: form.시작일,
          endDate: form.종료일,
          manager: saved?.담당자 ?? '',
          description: form.기획전설명,
          terms: form.이용약관,
          url: form.기획전URL,
          productKinds: saved?.상품종류 ?? '',
          closed: form.마감상태,
        });
        if (!mirrored.ok) {
          console.warn('기획전 미러링 실패:', mirrored.error);
          window.alert(
            '기획전은 저장되었지만 대리점 포털 노출용 동기화에 실패했습니다.\n'
            + '다시 저장하면 재시도됩니다.',
          );
        }
      }

      setFormOpen(false);
      setForm(null);
      await load();
    } catch (e) {
      // 타 업체 기획전 편집 시 SP가 RAISERROR로 거부한 문구가 그대로 올라온다.
      window.alert(e?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const name = row?.기획전명 || '';
    if (!window.confirm(`기획전 "${name}"을(를) 삭제하시겠습니까?\n연동된 상품가격 매핑도 함께 사라집니다.`)) return false;
    try {
      await deletePromotion({ promotionNo: row.기획전번호 });
      // 미러도 함께 정리한다. 남겨두면 대리점 포털에 유령 기획전이 계속 보인다.
      const mirrored = await mirrorPromotionDelete({ promotionNo: row.기획전번호 });
      if (!mirrored.ok) {
        console.warn('기획전 미러 삭제 실패:', mirrored.error);
        window.alert(
          '기획전은 삭제되었지만 대리점 포털에서 사라지지 않을 수 있습니다.\n'
          + '잠시 후 다시 시도해 주세요.',
        );
      }
      await load();
      return true;
    } catch (e) {
      window.alert(e?.message || '삭제에 실패했습니다.');
      return false;
    }
  };

  const openProducts = async (row) => {
    setProductsOpen(true);
    setProductsTarget(row);
    setProductsLoading(true);
    setProducts([]);
    try {
      const list = await getPromotionProducts({ promotionNo: row.기획전번호 });
      setProducts(Array.isArray(list) ? list : []);
    } catch (e) {
      window.alert(e?.message || '연동 상품을 불러오지 못했습니다.');
    } finally {
      setProductsLoading(false);
    }
  };

  return (
    <ConfigProvider locale={koKR}>
    <div className="p-4 space-y-4">
      <div className="rounded-2xl bg-white p-5 flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-[17px] font-bold text-gray-900">기획전 관리</h1>
        <button
          type="button"
          className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          onClick={() => openForm(0)}
          disabled={!myCompanyName}
          title={!myCompanyName ? '업체 정보를 확인한 후 등록할 수 있습니다.' : undefined}
        >
          새 기획전
        </button>
      </div>

      <div className="flex items-end gap-2 flex-wrap rounded-2xl bg-white p-5">
        <label className="text-sm text-gray-700">
          <span className="block mb-1 text-[13px] font-semibold text-gray-600">시작일 (기준)</span>
          <YmdDatePicker value={startDate} onChange={setStartDate} className="w-[148px]" />
        </label>
        <span className="pb-2 text-gray-400">~</span>
        <label className="text-sm text-gray-700">
          <span className="block mb-1 text-[13px] font-semibold text-gray-600">&nbsp;</span>
          <YmdDatePicker value={endDate} onChange={setEndDate} className="w-[148px]" />
        </label>
        <button
          type="button"
          className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50 inline-flex items-center"
          onClick={() => { setStartDate('1900-01-01'); setEndDate('2999-12-31'); }}
        >
          전체 기간
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-1 ml-2">
          <input type="checkbox" className="h-4 w-4" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />
          마감 포함
        </label>
        <input
          className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none ml-auto"
          placeholder="기획전명/담당자 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>

      {(loading || myCompanyName === undefined) && <div className="rounded-2xl bg-white py-16 text-center text-sm text-gray-500">로딩 중...</div>}
      {!loading && myCompanyName !== undefined && error && <div className="rounded-2xl bg-white py-16 text-center text-sm text-red-600">{error}</div>}

      {!loading && myCompanyName !== undefined && !error && (
        <div className="rounded-2xl bg-white p-5 overflow-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">기획전명</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">기간</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">담당자</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">상품종류</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-right">참여업체</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-right">등록상품</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-right">상품가격</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">마지막출발일</th>
                <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">상태</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filtered.map((r) => {
                const mine = isMine(r);
                return (
                <tr key={r.기획전번호} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-12 h-12 rounded border border-gray-200 bg-gray-50 overflow-hidden shrink-0">
                        {r.기획전주소 ? (
                          <img
                            src={r.기획전주소}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center text-[10px] text-gray-400 ${r.기획전주소 ? 'hidden' : ''}`}>
                          없음
                        </div>
                      </div>
                      {mine ? (
                        <button type="button" className="text-primary-700 hover:underline text-left min-w-0 truncate" onClick={() => openForm(r.기획전번호)}>
                          {r.기획전명 || '(이름 없음)'}
                        </button>
                      ) : (
                        <span className="text-gray-700 truncate">{r.기획전명 || '(이름 없음)'}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap text-gray-700">{r.시작일} ~ {r.종료일}</td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">{r.담당자 ?? ''}</td>
                  <td className="px-3 py-3 text-sm">{r.상품종류 || <span className="text-gray-400">미설정</span>}</td>
                  <td className="px-3 py-3 text-sm text-right">{r.참여업체수 ?? 0}</td>
                  <td className="px-3 py-3 text-sm text-right">
                    <button type="button" className="text-primary-700 hover:underline" onClick={() => openProducts(r)}>
                      {r.등록상품수 ?? 0}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-sm text-right">{r.상품가격수 ?? 0}</td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">{r.마지막출발일 || '-'}</td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap">
                    {r.마감상태
                      ? <span className="rounded-full px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600">마감</span>
                      : <span className="rounded-full px-2 py-0.5 text-xs font-semibold bg-green-50 text-green-700">진행중</span>}
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-sm text-gray-500">
                    {keyword.trim() ? '검색 결과가 없습니다.' : '등록한 기획전이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 편집 모달 */}
      {formOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setFormOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[min(46rem,92vw)] max-h-[90vh] flex flex-col">
            <div className="px-6 pt-5 pb-3">
              <h2 className="text-[17px] font-bold text-gray-900">{form?.기획전번호 ? '기획전 편집' : '새 기획전'}</h2>
            </div>

            {formLoading && <div className="px-6 py-16 text-center text-gray-500">불러오는 중...</div>}

            {!formLoading && form && (
              <div className="px-6 py-4 overflow-auto space-y-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1">기획전명 *</label>
                  <input
                    className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none w-full"
                    value={form.기획전명}
                    onChange={(e) => setField('기획전명', e.target.value)}
                    placeholder="예) 2026 여름 얼리버드"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">시작일 *</label>
                    <YmdDatePicker value={form.시작일} onChange={(v) => setField('시작일', v)} />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">종료일 *</label>
                    <YmdDatePicker value={form.종료일} onChange={(v) => setField('종료일', v)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1">담당자 *</label>
                    <select className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none w-full" value={form.담당자번호} onChange={(e) => setField('담당자번호', e.target.value)}>
                      <option value="">선택하세요</option>
                      {managers.map((m) => (
                        <option key={m.코드} value={String(m.코드)}>{m.코드명}</option>
                      ))}
                      {/* 목록에 없는 기존 담당자(퇴사 등)도 값은 유지되도록 보존 */}
                      {form.담당자번호 && !managers.some((m) => String(m.코드) === form.담당자번호) && (
                        <option value={form.담당자번호}>기존 담당자 (번호 {form.담당자번호})</option>
                      )}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                      <input type="checkbox" className="h-4 w-4" checked={form.마감상태} onChange={(e) => setField('마감상태', e.target.checked)} />
                      마감 처리
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1">기획전 이미지</label>
                  <div className="flex items-start gap-3">
                    <div className="w-40 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
                      {form.기획전URL ? (
                        <img
                          src={form.기획전URL}
                          alt="기획전 썸네일"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="text-xs text-gray-400 px-2 text-center">이미지 없음</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 min-w-0">
                      <div className="relative" ref={imageMenuRef}>
                        <button
                          type="button"
                          className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                          disabled={imageUploading || saving}
                          onClick={() => setImageMenuOpen((v) => !v)}
                        >
                          {imageUploading ? '업로드 중...' : '이미지 선택'}
                        </button>
                        {imageMenuOpen && (
                          <div className="absolute left-0 top-full mt-1 z-[60] w-[280px] max-w-[80vw] bg-white border rounded shadow-lg">
                            <div className="p-2">
                              <div className="px-2 py-1 text-[11px] text-gray-500">이미지 선택 방법</div>
                              <button type="button" className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 text-xs" onClick={handlePromoUploadFromPc}>내 PC에서 업로드</button>
                              <button type="button" className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 text-xs" onClick={openPromoCommonModal}>공용 이미지에서 선택</button>
                              <button type="button" className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 text-xs" onClick={openPromoGalleryModal}>상품 갤러리에서 선택</button>
                            </div>
                          </div>
                        )}
                      </div>
                      {form.기획전URL && (
                        <button
                          type="button"
                          className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 self-start"
                          disabled={imageUploading || saving}
                          onClick={() => setPromoImageUrl('')}
                        >
                          이미지 제거
                        </button>
                      )}
                      <p className="text-[11px] text-gray-500">선택한 이미지가 대리점 판매 시스템에 노출됩니다</p>
                    </div>
                  </div>
                  <input
                    ref={imageFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPromoImageFileChange}
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1">기획전 설명</label>
                  <textarea className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm h-20 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none" value={form.기획전설명} onChange={(e) => setField('기획전설명', e.target.value)} />
                </div>

                {/* 이용약관·상품종류별 수수료 UI는 숨김 — 저장 시 기존 값은 그대로 전송 */}
              </div>
            )}

            <div className="px-6 pb-5 pt-3 flex items-center justify-between gap-3">
              <div>
                {!!form?.기획전번호 && (
                  <button
                    type="button"
                    className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                    disabled={saving || formLoading}
                    onClick={async () => {
                      setSaving(true);
                      try {
                        const ok = await remove({ 기획전번호: form.기획전번호, 기획전명: form.기획전명 });
                        if (ok) {
                          setFormOpen(false);
                          setForm(null);
                        }
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    삭제
                  </button>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => setFormOpen(false)} disabled={saving}>취소</button>
                <button
                  type="button"
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  onClick={submit}
                  disabled={saving || formLoading || !form}
                >
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 연동 상품 모달 */}
      {productsOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setProductsOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[min(64rem,94vw)] max-h-[90vh] flex flex-col">
            <div className="px-6 pt-5 pb-3">
              <h2 className="text-[17px] font-bold text-gray-900">연동 상품 — {productsTarget?.기획전명}</h2>
              <p className="text-xs text-gray-500 mt-1">
                연동된 개별 날짜는 상품별 가격/재고 화면에서 확인·변경할 수 있습니다.
              </p>
            </div>

            {productsLoading && <div className="px-6 py-16 text-center text-gray-500">불러오는 중...</div>}

            {!productsLoading && (
              <div className="px-6 py-4 overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">공급업체</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">상품종류</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">상품명</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">상품자원</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">가격옵션</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-right">연동 날짜수</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">마지막출발일</th>
                      <th className="h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, idx) => (
                      <tr key={`${p.상품번호}-${p.자원번호}-${p.가격옵션번호}-${idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-3 py-3 whitespace-nowrap">{p.업체명 ?? ''}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{p.상품종류 ?? ''}</td>
                        <td className="px-3 py-3">{p.상품명 ?? ''}</td>
                        <td className="px-3 py-3">{p.상품자원명 ?? ''}</td>
                        <td className="px-3 py-3">{p.가격옵션명 ?? ''}</td>
                        <td className="px-3 py-3 text-right">{p.가격갯수 ?? 0}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{p.마지막출발일 || '-'}</td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                            onClick={() => { setProductsOpen(false); navigate(`/price-stock/${p.상품번호}`); }}
                          >
                            가격/재고
                          </button>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-16 text-center text-sm text-gray-500">연동된 상품이 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="px-6 pb-5 pt-3 flex justify-end gap-2">
              <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => setProductsOpen(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 공용 이미지 선택 */}
      {commonOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCommonOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h4 className="text-[17px] font-bold text-gray-900">공용 이미지 선택</h4>
              <button type="button" className="h-9 rounded-lg px-3 text-sm font-semibold text-gray-500 hover:bg-gray-100" onClick={() => setCommonOpen(false)}>닫기</button>
            </div>
            <div className="px-6 py-3 flex items-center gap-2">
              <label className="text-xs text-gray-600">도시</label>
              <div className="relative">
                <button
                  type="button"
                  className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm flex items-center gap-1 min-w-[120px]"
                  onClick={() => setCommonCityOpen((v) => !v)}
                >
                  <span className="flex-1 text-left">{commonCityName || '선택'}</span>
                  <span className="text-gray-400">▼</span>
                </button>
                {commonCityOpen && (
                  <div className="absolute top-full left-0 mt-1 w-[240px] max-h-[320px] overflow-auto bg-white border rounded shadow-lg z-10">
                    <div className="sticky top-0 p-2 border-b bg-white">
                      <input
                        type="text"
                        className="w-full px-2 py-1 text-xs border rounded"
                        placeholder="도시명 검색"
                        value={commonCityQuery}
                        onChange={(e) => setCommonCityQuery(e.target.value)}
                      />
                    </div>
                    <div className="p-1">
                      {(() => {
                        const query = (commonCityQuery || '').toLowerCase();
                        const filtered = query
                          ? commonCities.filter((c) => {
                            const name = String(c?.코드명 || '').toLowerCase();
                            const code = String(c?.코드 || '').toLowerCase();
                            return name.includes(query) || code.includes(query);
                          })
                          : commonCities;
                        if (!filtered.length) {
                          return <div className="p-2 text-xs text-gray-500">검색 결과 없음</div>;
                        }
                        return filtered.map((city) => {
                          const cityNo = String(city?.코드 || '');
                          const cityName = String(city?.코드명 || '');
                          return (
                            <button
                              key={cityNo}
                              type="button"
                              className={`block w-full px-3 py-1.5 text-xs text-left hover:bg-gray-100 ${cityNo === commonCityNo ? 'bg-blue-50 text-blue-600' : ''}`}
                              onClick={async () => {
                                setCommonCityNo(cityNo);
                                setCommonCityName(cityName);
                                setCommonCityOpen(false);
                                try {
                                  setCommonLoading(true);
                                  const imgs = await getCommonImagesByCity({ cityNo });
                                  setCommonImgs(Array.isArray(imgs) ? imgs : []);
                                } catch (err) {
                                  console.error(err);
                                  window.alert('도시별 공용 이미지를 불러오지 못했습니다.');
                                } finally {
                                  setCommonLoading(false);
                                }
                              }}
                            >
                              {cityName}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-auto px-6 py-4">
              {commonLoading ? (
                <div className="text-sm text-gray-500 py-10 text-center">불러오는 중...</div>
              ) : !commonImgs.length ? (
                <div className="text-sm text-gray-500">이미지가 없습니다. 다른 도시를 선택해주세요.</div>
              ) : (
                <div className="grid grid-cols-6 gap-3">
                  {commonImgs.map((img) => {
                    const src = imageSrcOf(img);
                    return (
                      <button
                        key={img.이미지번호}
                        type="button"
                        className="block w-full aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-blue-400"
                        onClick={() => setPromoImageUrl(src)}
                        title={img.이미지명 || ''}
                      >
                        <img src={src} alt={img.이미지명 || ''} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 상품 갤러리 선택 */}
      {galleryOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setGalleryOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h4 className="text-[17px] font-bold text-gray-900">상품 갤러리에서 선택</h4>
              <button type="button" className="h-9 rounded-lg px-3 text-sm font-semibold text-gray-500 hover:bg-gray-100" onClick={() => setGalleryOpen(false)}>닫기</button>
            </div>
            <div className="flex-1 overflow-auto px-6 py-4">
              {galleryLoading ? (
                <div className="text-sm text-gray-500 py-10 text-center">불러오는 중...</div>
              ) : !galleryImgs.length ? (
                <div className="text-sm text-gray-500">이미지가 없습니다.</div>
              ) : (
                <div className="grid grid-cols-6 gap-3">
                  {galleryImgs.map((img) => {
                    const src = imageSrcOf(img);
                    return (
                      <button
                        key={img.이미지번호}
                        type="button"
                        className="block w-full aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-emerald-400"
                        onClick={() => setPromoImageUrl(src)}
                        title={img.이미지명 || ''}
                      >
                        <img src={src} alt={img.이미지명 || ''} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </ConfigProvider>
  );
};

export default PromotionManagement;
